// 004 (data-model §6; research R14): client-side checks before sending; the server stays the
// authority and its field errors are shown on the same fields.
import type { Api } from "@/api/types";

const CONTROL = /[\u0000-\u001f\u007f]/;

function length(
  value: string,
  min: number,
  max: number,
  empty: string,
): string | null {
  const v = value.trim();
  if (v.length < min) return empty;
  if (v.length > max) return `Use ${max} characters or fewer.`;
  return null;
}

export function validateName(name: string): string | null {
  if (CONTROL.test(name)) return "Remove the special characters.";
  return length(name, 1, 100, "Enter a name.");
}

export type MobileResult =
  { ok: true; value: string | null } | { ok: false; error: string };

/** Empty → null; "+91 98480 22338", "098480 22338" → "9848022338"; else an error. */
export function normaliseMobile(input: string): MobileResult {
  let digits = input.replace(/[\s-]/g, "");
  if (digits === "") return { ok: true, value: null };
  if (digits.startsWith("+91")) digits = digits.slice(3);
  else if (digits.length === 11 && digits.startsWith("0"))
    digits = digits.slice(1);
  if (/^[6-9]\d{9}$/.test(digits)) return { ok: true, value: digits };
  return { ok: false, error: "Enter a 10-digit Indian mobile number." };
}

export type AddressDraft = {
  label: Api["AddressLabel"];
  customLabel: string;
  flatHouse: string;
  streetArea: string;
  landmark: string;
  city: string;
  pincode: string;
  pinPlaced: boolean;
};

export type AddressErrors = Partial<
  Record<
    | "customLabel"
    | "flatHouse"
    | "streetArea"
    | "landmark"
    | "city"
    | "pincode"
    | "pin",
    string
  >
>;

export function validateAddress(d: AddressDraft): AddressErrors {
  const errors: AddressErrors = {};
  const put = (k: keyof AddressErrors, e: string | null) => {
    if (e) errors[k] = e;
  };
  if (d.label === "OTHER")
    put(
      "customLabel",
      length(d.customLabel, 1, 30, "Enter a name for this address."),
    );
  put(
    "flatHouse",
    length(d.flatHouse, 1, 100, "Enter the flat or house number."),
  );
  put("streetArea", length(d.streetArea, 1, 150, "Enter the street or area."));
  put("landmark", length(d.landmark, 0, 100, ""));
  put("city", length(d.city, 1, 60, "Enter the city."));
  if (!/^[1-9][0-9]{5}$/.test(d.pincode.trim()))
    errors.pincode = "Enter a 6-digit pincode.";
  if (!d.pinPlaced) errors.pin = "Move the map to place the pin.";
  return errors;
}
